import type { Escrow } from "../../repository/EscrowRepository.ts";
import { formatAddress } from "../../utils/formatAddress.ts";
import { formatUnits } from "viem";
import { useNavigate } from "react-router";
import formatAmount from "../../utils/millify.ts";
import { getFormattedStatus } from "../../utils/getFormattedStatus.ts";

const LoadingEscrowTables = () => {
  return (
    <tbody>
      {Array.from({ length: 2 }, (_, index) => (
        <tr key={index}>
          <td>
            <div className="skeleton h-10 w-full" />
          </td>
          <td className="w-full">
            <div className="skeleton h-10 w-full" />
          </td>
          <td className="w-full">
            <div className="skeleton h-10 w-full" />
          </td>
          <td className="w-full">
            <div className="skeleton h-10 w-full" />
          </td>
          <td className="w-full">
            <div className="skeleton h-10 w-full" />
          </td>
          <td className="w-full">
            <div className="skeleton h-10 w-full" />
          </td>
        </tr>
      ))}
    </tbody>
  );
};

const EscrowRow = ({
  escrow,
  onClick,
}: {
  escrow: Escrow;
  onClick: (escrow: Escrow) => void;
}) => {
  const buyerAddress =
    formatAddress(escrow.buyer_account, true) +
    (escrow.buyer_nickname ? ` (${escrow.buyer_nickname})` : "");
  const sellerAddress =
    formatAddress(escrow.seller_account, true) +
    (escrow.seller_nickname ? ` (${escrow.seller_nickname})` : "");

  const exchangeLabel = `${formatAmount(formatUnits(escrow.buyer_exchange_amount, escrow.buyer_exchange_token.decimals))} ${escrow.buyer_exchange_token.symbol} ↔ ${formatAmount(formatUnits(escrow.seller_exchange_amount, escrow.seller_exchange_token.decimals))} ${escrow.seller_exchange_token.symbol}`;
  //const depositLabel = `${formatAmount(formatUnits(escrow.buyer_penalty_amount, escrow.buyer_penalty_token.decimals))} ${escrow.buyer_penalty_token.symbol} | ${formatAmount(formatUnits(escrow.seller_penalty_amount, escrow.seller_penalty_token.decimals))} ${escrow.seller_penalty_token.symbol}`;

  const formattedStatus = getFormattedStatus(escrow.status);

  return (
    <tr
      className="hover:bg-base-300 cursor-pointer"
      onClick={() => onClick(escrow)}
    >
      <td className="w-[120px] truncate">{buyerAddress}</td>
      <td className="w-[120px] truncate">{sellerAddress}</td>
      <td className="w-[200px] truncate">{exchangeLabel}</td>
      <td className="w-[160px] truncate">
        {new Date(escrow.created_at).toLocaleString()}
      </td>
      <td>
        <div className={`badge badge-soft ${formattedStatus.color}`}>
          {formattedStatus.name}
        </div>
      </td>
    </tr>
  );
};

const EscrowsTables = ({
  isLoading,
  escrows,
}: {
  isLoading: boolean;
  escrows: Escrow[];
}) => {
  const navigate = useNavigate();

  const handleRowClick = (escrow: Escrow) => {
    navigate(`/escrow/${escrow.escrow_address}`);
  };

  return (
    <div className="card bg-base-100">
      <div className="card-body">
        <div className="overflow-x-auto">
          <table className="table w-full table-fixed">
            <thead>
              <tr>
                <th>Buyer</th>
                <th>Seller</th>
                <th>Exchange</th>
                <th>Creation date</th>
                <th className={"w-[180px]"}>Status</th>
              </tr>
            </thead>
            {isLoading ? (
              <LoadingEscrowTables />
            ) : (
              <tbody>
                {escrows.map((escrow, index) => (
                  <EscrowRow
                    key={index}
                    escrow={escrow}
                    onClick={handleRowClick}
                  />
                ))}
              </tbody>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default EscrowsTables;
